import { forwardRef, type HTMLAttributes } from 'react';
import { Check } from 'lucide-react';
import { cn } from '../lib/cn';

export interface ProgressStepsProps extends HTMLAttributes<HTMLDivElement> {
  steps: string[];
  currentStep: number; // 0-indexed
}

export const ProgressSteps = forwardRef<HTMLDivElement, ProgressStepsProps>(
  ({ steps, currentStep, className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn("flex flex-col sm:flex-row w-full justify-between", className)}
        {...props}
      >
        {steps.map((step, index) => {
          const isCompleted = index < currentStep;
          const isCurrent = index === currentStep;
          const isLast = index === steps.length - 1;

          return (
            <div
              key={step}
              className={cn(
                "relative flex items-center sm:flex-col sm:flex-1",
                !isLast && "mb-4 sm:mb-0"
              )}
            >
              {/* Connector line for mobile (vertical) */}
              {!isLast && (
                <div
                  className={cn(
                    "absolute left-4 top-8 -bottom-4 w-[2px] sm:hidden",
                    isCompleted ? "bg-success" : "bg-border border-dashed border-l-2 border-border/0"
                  )}
                  style={{ borderLeftColor: isCompleted ? 'transparent' : '' }}
                />
              )}

              {/* Connector line for desktop (horizontal) */}
              {!isLast && (
                <div
                  className={cn(
                    "absolute top-4 left-[50%] w-full h-[2px] hidden sm:block",
                    isCompleted ? "bg-success" : "bg-border border-dashed border-t-2 border-border/0"
                  )}
                  style={{ borderTopColor: isCompleted ? 'transparent' : '' }}
                />
              )}

              <div
                className={cn(
                  "relative z-10 flex h-8 w-8 items-center justify-center rounded-full border-2 bg-background font-semibold text-sm shrink-0",
                  isCompleted
                    ? "border-success bg-success text-success-foreground"
                    : isCurrent
                    ? "border-primary text-primary animate-pulse shadow-[0_0_0_4px_rgba(var(--primary),0.1)]"
                    : "border-muted text-muted-foreground"
                )}
              >
                {isCompleted ? <Check className="h-4 w-4" /> : index + 1}
              </div>
              
              <div
                className={cn(
                  "ml-4 sm:ml-0 sm:mt-2 text-sm sm:text-center",
                  isCurrent ? "font-bold text-foreground" : isCompleted ? "font-medium text-foreground" : "text-muted-foreground"
                )}
              >
                {step}
              </div>
            </div>
          );
        })}
      </div>
    );
  }
);
ProgressSteps.displayName = "ProgressSteps";
